import { Navigate, Outlet } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import apiEndpoints from "../../apiconfig";
import { ShieldAlert, Clock, LogOut, Handshake } from "lucide-react";

const IDLE_LIMIT = 15 * 60 * 1000; // 15 minutes
const COUNTDOWN_TIME = 30; // seconds
const TOKEN_CHECK_INTERVAL = 60 * 1000; // 1 minute

const ProtectedRoute = () => {
    const [authorized, setAuthorized] = useState(null);
    const [showIdleModal, setShowIdleModal] = useState(false);
    const [countdown, setCountdown] = useState(COUNTDOWN_TIME);

    const validatingRef = useRef(false);
    const idleTimeoutRef = useRef(null);
    const countdownRef = useRef(null);
    const tokenIntervalRef = useRef(null);

    /* ---------------- TOKEN VALIDATION ---------------- */
    const validateToken = async () => {
        if (validatingRef.current) return;
        validatingRef.current = true;

        const token = localStorage.getItem("parent_token");
        if (!token) {
            logout();
            return;
        }

        try {
            const res = await fetch(apiEndpoints.validateToken, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const data = await res.json();

            if (data.success) {
                if (data.new_token) {
                    localStorage.setItem("token", data.new_token);
                }
                setAuthorized(true);
            } else {
                logout();
            }
        } catch {
            logout();
        } finally {
            validatingRef.current = false;
        }
    };

    /* ---------------- LOGOUT ---------------- */
    const logout = () => {
        localStorage.clear();
        clearTimers();
        setAuthorized(false);
        window.location.href = "/";
    };

    /* ---------------- IDLE TIMER ---------------- */
    const resetIdleTimer = () => {
        if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
        if (countdownRef.current) clearInterval(countdownRef.current);

        setShowIdleModal(false);
        setCountdown(COUNTDOWN_TIME);

        idleTimeoutRef.current = setTimeout(() => {
            setShowIdleModal(true);

            countdownRef.current = setInterval(() => {
                setCountdown((prev) => {
                    if (prev <= 1) {
                        logout();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }, IDLE_LIMIT);
    };

    const clearTimers = () => {
        if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
        if (countdownRef.current) clearInterval(countdownRef.current);
        if (tokenIntervalRef.current) clearInterval(tokenIntervalRef.current);
    };

    /* ---------------- USER ACTIVITY ---------------- */
    useEffect(() => {
  if (!authorized) return;

  const events = ["mousemove", "keydown", "scroll", "touchstart"];
  const handleActivity = () => {
    if (showIdleModal) return;
    resetIdleTimer();
  };

  events.forEach((e) => window.addEventListener(e, handleActivity));

  return () => {
    events.forEach((e) => window.removeEventListener(e, handleActivity));
  };
}, [authorized, showIdleModal]);

    /* ---------------- INIT ---------------- */
    useEffect(() => {
        validateToken();
        tokenIntervalRef.current = setInterval(validateToken, TOKEN_CHECK_INTERVAL);

        return () => clearTimers();
    }, []);

    /* ---------------- UI STATES ---------------- */
    if (authorized === null) {
        return (
            <div style={{ textAlign: "center", marginTop: 80 }}>
                Checking authentication…
            </div>
        );
    }

    if (!authorized) {
        return <Navigate to="/" replace />;
    }

    return (
        <>
            <Outlet />

            {showIdleModal && (
                <div style={overlayStyle}>
                    <div style={modalStyle}>
                        <div style={headerStyle}>
                            <ShieldAlert size={24} color="#2563eb" />
                            <h3>Session Timeout Warning</h3>
                        </div>

                        <div style={{ padding: 20, textAlign: "center" }}>
                            <Clock size={40} color="#2563eb" />
                            <p>
                                You’ll be logged out in <b>{countdown}</b> seconds due to inactivity.
                            </p>

                            <div style={buttonRow}>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        resetIdleTimer();
                                    }}
                                    style={stayBtn}
                                >
                                    <Handshake size={18} /> Stay Logged In
                                </button>

                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        logout();
                                    }}
                                    style={logoutBtn}
                                >
                                    <LogOut size={18} /> Sign Out
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ProtectedRoute;

/* ---------------- STYLES ---------------- */
const overlayStyle = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
};

const modalStyle = {
    background: "#fff",
    width: 420,
    borderRadius: 16,
    boxShadow: "0 10px 30px rgba(0,0,0,.2)",
    overflow: "hidden",
};

const headerStyle = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: 20,
    background: "#E0F2FE",
};

const buttonRow = {
    display: "flex",
    justifyContent: "center",
    gap: 12,
    marginTop: 24,
};

const stayBtn = {
    background: "#2563eb",
    color: "#fff",
    padding: "10px 18px",
    borderRadius: 8,
    border: "none",
    cursor: "pointer",
};

const logoutBtn = {
    background: "#ef4444",
    color: "#fff",
    padding: "10px 18px",
    borderRadius: 8,
    border: "none",
    cursor: "pointer",
};
